import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Loading,
  ProgressBar,
  ProgressIndicator,
  ProgressStep,
} from "@carbon/react";
import { ArrowLeft, ChevronLeft, ChevronRight } from "@carbon/icons-react";
import { usePublicCountries } from "../../hooks/useCountries";
import { useCompanyProfile } from "../../hooks/useCompany";
import { useUser } from "../../context";
import BasicInformationForm from "./BasicInformationForm";
import AddressesForm from "./AddressesForm";
import ContactsInformationForm from "./ContactsInformationForm";
import CertificationsForm from "./CertificationsForm";
import MainClientsForm from "./MainClientsForm";
import BankingInformationForm from "./BankingInformationForm";
import FilesForm from "./FilesForm";
import {
  calculateProfileCompletion,
  calculateStepCompletion,
  getCompletionProgressBarStatus,
} from "./companyProfileCompletion";
import "./companyProfileForm.scss";

/**
 * The company profile edit WIZARD SHELL (`/company/edit`, seller-only via the route tree), rebuilt
 * on Carbon (Task 7, Profile/Company flow) in place of 383 lines of Tailwind. This is the SHELL
 * only — step navigation/progress, the two `.cds-form-surface` panels, and the Previous/Next
 * buttons. The 7 sub-forms it renders as step bodies (`BasicInformationForm`, `AddressesForm`,
 * `BankingInformationForm`, `CertificationsForm`, `FilesForm`, `ContactsInformationForm`,
 * `MainClientsForm`) are each still Tailwind and are NOT touched here — Tasks 8-10 migrate them.
 * Carbon chrome around Tailwind step content is the expected mid-migration state, not a defect.
 *
 * There is NO single submit: each sub-form saves independently via its own
 * `useUpdateCompanyProfile` call (`PUT /company/{id}/profile`) — this shell only owns which step
 * is showing, the shared `isDirty` flag every sub-form's `setIsDirty` writes into, and the
 * `beforeunload`/step-change warning built on it. All three are carried over unchanged from the
 * pre-Carbon version, including the exact `window.confirm` gate on both Next/Previous and jumping
 * to an arbitrary step.
 *
 * Step nav: Carbon `ProgressIndicator`/`ProgressStep`, `currentIndex`-driven — `complete`/`current`
 * per step come from `ProgressIndicator` itself (any index below `currentIndex` renders complete),
 * so unlike the pre-Carbon version this shell no longer computes that by hand. Each step's
 * `secondaryLabel` still shows its own real-data completion percentage
 * (`calculateStepCompletion`), and clicking any step (not just next/prev) still jumps straight to
 * it, exactly like the pre-Carbon circle buttons did. The overall completion bar is Carbon
 * `ProgressBar`, coloured via `getCompletionProgressBarStatus` — the Carbon-native replacement for
 * the old Tailwind `getBorderColor`/`getBackgroundColor` threshold helpers, all three (plus
 * `calculateProfileCompletion`) now living in `companyProfileCompletion.ts` so they're unit-tested
 * without mounting this component. `CompletionPercentage.tsx` (a separate, still-Tailwind shared
 * component, unmodified here beyond its import) consumes the same `calculateProfileCompletion`.
 *
 * Back affordance: `GoBackArrow` (Tailwind) replaced with a plain Carbon ghost `Button`, the same
 * swap `EditSite.tsx` (Task 6 addendum) already made — `navigate(-1)` matches `GoBackArrow`'s own
 * default behaviour on this route (no `isOffer`/`/material/offer` special case applies here).
 */
const WIZARD_STEPS = [
  { step: 1, labelKey: "companyProfile.basicInfo" },
  { step: 2, labelKey: "companyProfile.companyData" },
  { step: 3, labelKey: "companyProfile.contacts" },
  { step: 4, labelKey: "companyProfile.mainClients" },
] as const;

const TOTAL_STEPS = WIZARD_STEPS.length;

const CompanyProfileForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const companyId = user?.company?.id || "";

  const { data: companyProfile, isLoading: isLoadingProfile } =
    useCompanyProfile(companyId);
  const { data: countries } = usePublicCountries();

  const [currentStep, setCurrentStep] = useState(1);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = t("companyProfile.unsavedChanges");
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, t]);

  /** Moves to `step`, gated behind the same unsaved-changes confirmation the pre-Carbon
   * Next/Previous buttons and step circles each had their own copy of. A no-op if `step` is
   * already current, or if the user cancels the confirmation. */
  const goToStep = (step: number) => {
    if (step === currentStep) return;
    if (isDirty && !window.confirm(t("companyProfile.unsavedChanges"))) {
      return;
    }
    setIsDirty(false);
    setCurrentStep(step);
  };

  if (isLoadingProfile) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  const overallCompletion = companyProfile
    ? calculateProfileCompletion(companyProfile)
    : 0;

  const renderStepContent = () => {
    if (!companyProfile) return null;

    switch (currentStep) {
      case 1:
        return (
          <BasicInformationForm
            setIsDirty={setIsDirty}
            profile={companyProfile}
          />
        );

      case 2:
        return (
          <div className="company-profile-form__step-group">
            <AddressesForm
              profile={companyProfile}
              countries={countries || []}
              setIsDirty={setIsDirty}
            />
            <BankingInformationForm
              profile={companyProfile}
              countries={countries || []}
              setIsDirty={setIsDirty}
            />
            <CertificationsForm
              profile={companyProfile}
              setIsDirty={setIsDirty}
            />
            <FilesForm profile={companyProfile} setIsDirty={setIsDirty} />
          </div>
        );

      case 3:
        return (
          <ContactsInformationForm
            profile={companyProfile}
            setIsDirty={setIsDirty}
          />
        );

      case 4:
        return (
          <MainClientsForm profile={companyProfile} setIsDirty={setIsDirty} />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Button
        kind="ghost"
        size="sm"
        renderIcon={ArrowLeft}
        onClick={() => navigate(-1)}
        className="company-profile-form__back"
      >
        {t("cta.goBack")}
      </Button>

      <div className="cds-form-surface">
        {companyProfile && (
          <div className="company-profile-form__completion">
            <ProgressBar
              label={t("companyProfile.profileCompletion")}
              value={overallCompletion}
              max={100}
              helperText={`${overallCompletion}%`}
              status={getCompletionProgressBarStatus(overallCompletion)}
            />
          </div>
        )}

        <ProgressIndicator
          currentIndex={currentStep - 1}
          spaceEqually
          onChange={(index) => goToStep(index + 1)}
          className="company-profile-form__steps"
        >
          {WIZARD_STEPS.map(({ step, labelKey }) => (
            <ProgressStep
              key={step}
              label={t(labelKey)}
              secondaryLabel={`${calculateStepCompletion(companyProfile, step)}%`}
            />
          ))}
        </ProgressIndicator>
      </div>

      <div className="cds-form-surface company-profile-form__panel">
        {renderStepContent()}
      </div>

      <div className="company-profile-form__actions">
        <div>
          {currentStep > 1 && (
            <Button
              kind="tertiary"
              renderIcon={ChevronLeft}
              onClick={() => goToStep(currentStep - 1)}
            >
              {t("cta.previous")}
            </Button>
          )}
        </div>
        <div>
          {currentStep < TOTAL_STEPS && (
            <Button
              kind="primary"
              renderIcon={ChevronRight}
              onClick={() => goToStep(currentStep + 1)}
            >
              {t("cta.next")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfileForm;
